import React from "react";

const WHATSAPP_CONTACT_CHANNELS = new Set(["whatsapp", "whatsappbusiness", "","whatsapp_business_account"]);
const BLOCKED_BULK_NUMBERS = new Set(["status"]);

function bulkPhoneDigits(number) {
	return String(number || "").split("@")[0].replace(/\D/g, "");
}

export function bulkContactKey(contact) {
	return bulkPhoneDigits(contact?.number);
}

export function isPlausibleWhatsappPhoneDigits(digits) {
	if (!digits || BLOCKED_BULK_NUMBERS.has(digits)) {
		return false;
	}

	if (digits.length < 10 || digits.length > 13) {
		return false;
	}

	if (/^55[1-9]\d{9,10}$/.test(digits)) {
		return true;
	}

	if (/^[1-9]\d{9,10}$/.test(digits)) {
		const ddd = Number.parseInt(digits.slice(0, 2), 10);
		return ddd >= 11 && ddd <= 99;
	}

	return false;
}

export function isWhatsappBulkContact(contact) {
	if (!contact || contact.isGroup) {
		return false;
	}

	const contactChannel = String(contact?.channel || "").toLowerCase();
	if (contactChannel && !WHATSAPP_CONTACT_CHANNELS.has(contactChannel)) {
		return false;
	}

	const jid = String(contact?.jid || "").toLowerCase();
	if (
		jid.includes("status@broadcast") ||
		jid.includes("@broadcast") ||
		jid.endsWith("@lid")
	) {
		return false;
	}

	return isPlausibleWhatsappPhoneDigits(bulkPhoneDigits(contact?.number));
}

export function normalizeBulkPhoneDigits(number) {
	const raw = String(number || "").trim();
	let digits = raw.split("@")[0].replace(/\D/g, "");
	if (!digits) return "";

	if (digits.startsWith("00")) {
		digits = digits.replace(/^00+/, "");
	}

	if (digits.startsWith("0") && digits.length >= 11) {
		digits = digits.replace(/^0+/, "");
	}

	if (digits.startsWith("55")) {
		return digits.slice(0, 13);
	}

	if (digits.length === 10 || digits.length === 11) {
		return `55${digits}`.slice(0, 13);
	}

	return digits.slice(0, 15);
}

export function isValidBulkManualPhone(number) {
	const digits = normalizeBulkPhoneDigits(number);
	return Boolean(digits) && isPlausibleWhatsappPhoneDigits(digits);
}

export function getBulkPhoneValidationError(rawNumber) {
	const raw = String(rawNumber || "").trim();
	if (!raw) return "emptyNumber";

	const normalized = normalizeBulkPhoneDigits(raw);
	if (!normalized || !isPlausibleWhatsappPhoneDigits(normalized)) {
		return "invalidNumber";
	}

	return null;
}

const NAME_HEADERS = ["name", "nome", "nombre"];
const NUMBER_HEADERS = [
	"number",
	"numero",
	"número",
	"telefone",
	"phone",
	"celular",
	"telefono",
];

const cleanCsvCell = (value) =>
	String(value || "")
		.trim()
		.replace(/^["']|["']$/g, "");

/** Excel costuma devolver telefone como number; evita notação científica. */
const cellToString = (value) => {
	if (value == null || value === "") return "";
	if (typeof value === "number" && Number.isFinite(value)) {
		return String(Math.round(value));
	}
	if (typeof value === "boolean") return value ? "true" : "false";
	if (value instanceof Date) return String(value.getTime());
	return cleanCsvCell(value);
};

const detectCsvDelimiter = (headerLine) => {
	const semicolons = (headerLine.match(/;/g) || []).length;
	const commas = (headerLine.match(/,/g) || []).length;
	return semicolons > commas ? ";" : ",";
};

function parseContactTable(headers, dataRows) {
	const normalizedHeaders = (headers || []).map((header) =>
		cleanCsvCell(header).toLowerCase()
	);
	const nameIdx = normalizedHeaders.findIndex((header) =>
		NAME_HEADERS.includes(header)
	);
	const numberIdx = normalizedHeaders.findIndex((header) =>
		NUMBER_HEADERS.includes(header)
	);

	if (nameIdx === -1 || numberIdx === -1) {
		return { error: "invalidColumns" };
	}

	const contacts = [];
	const rejected = [];
	const seenNumbers = new Set();

	for (let i = 0; i < dataRows.length; i += 1) {
		const cols = dataRows[i] || [];
		if (!cols.length || cols.every((col) => !cellToString(col))) continue;

		const line = i + 2;
		const name = cellToString(cols[nameIdx]);
		const rawNumber = cellToString(cols[numberIdx]);

		if (!name) {
			rejected.push({ line, name: "-", rawNumber, reason: "emptyName" });
			continue;
		}

		if (!rawNumber) {
			rejected.push({ line, name, rawNumber: "-", reason: "emptyNumber" });
			continue;
		}

		const validationError = getBulkPhoneValidationError(rawNumber);
		if (validationError) {
			rejected.push({ line, name, rawNumber, reason: validationError });
			continue;
		}

		const number = normalizeBulkPhoneDigits(rawNumber);
		if (seenNumbers.has(number)) {
			rejected.push({ line, name, rawNumber, reason: "duplicate" });
			continue;
		}

		seenNumbers.add(number);
		contacts.push({ name, number });
	}

	if (!contacts.length && !rejected.length) {
		return { error: "noRows" };
	}

	return { contacts, rejected };
}

export function parseCsvContacts(rawText) {
	let normalizedCsv = String(rawText || "")
		.replace(/^\uFEFF/, "")
		.replace(/\r/g, "");

	normalizedCsv += "\n";
	const lines = normalizedCsv.split("\n").filter((line) => line.trim());

	if (lines.length < 2) {
		return { error: "empty" };
	}

	const delimiter = detectCsvDelimiter(lines[0]);
	const headers = lines[0].split(delimiter);
	const dataRows = lines.slice(1).map((line) => line.split(delimiter));
	return parseContactTable(headers, dataRows);
}

/**
 * Lê a primeira planilha de .xlsx / .xls (SheetJS).
 * Espera colunas name/nome e number/numero (mesmas regras do CSV).
 */
export async function parseSpreadsheetContacts(arrayBuffer) {
	const XLSX = await import("xlsx");
	const workbook = XLSX.read(arrayBuffer, {
		type: "array",
		cellDates: false,
		raw: true,
	});

	const sheetName = workbook.SheetNames?.[0];
	if (!sheetName) {
		return { error: "empty" };
	}

	const sheet = workbook.Sheets[sheetName];
	const rows = XLSX.utils.sheet_to_json(sheet, {
		header: 1,
		defval: "",
		raw: true,
		blankrows: false,
	});

	if (!Array.isArray(rows) || rows.length < 2) {
		return { error: "empty" };
	}

	const headers = rows[0] || [];
	const dataRows = rows.slice(1);
	return parseContactTable(headers, dataRows);
}

export function isBulkContactsSpreadsheetFile(fileName = "") {
	const ext = String(fileName).split(".").pop()?.toLowerCase();
	return ext === "xlsx" || ext === "xls";
}

export function isBulkContactsImportFile(fileName = "") {
	const ext = String(fileName).split(".").pop()?.toLowerCase();
	return ext === "csv" || ext === "xlsx" || ext === "xls";
}

export function formatBulkPhoneInputDisplay(number) {
	const digits = normalizeBulkPhoneDigits(number);
	if (!digits) return "";

	const complete = formatBulkPhone(digits);
	if (complete) return complete;

	if (digits.startsWith("55")) {
		const national = digits.slice(2);
		if (national.length === 0) return "+55 ";
		if (national.length <= 2) {
			return `+55 (${national}`;
		}
		if (national.length <= 6) {
			return `+55 (${national.slice(0, 2)}) ${national.slice(2)}`;
		}
		if (national.length <= 10) {
			return `+55 (${national.slice(0, 2)}) ${national.slice(2, 6)}-${national.slice(6)}`;
		}
		return `+55 (${national.slice(0, 2)}) ${national.slice(2, 7)}-${national.slice(7)}`;
	}

	if (digits.length <= 2) return digits;
	if (digits.length <= 6) {
		return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
	}
	if (digits.length <= 10) {
		return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
	}
	return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export function formatBulkPhone(number) {
	const digits = normalizeBulkPhoneDigits(number);
	if (!digits || !isPlausibleWhatsappPhoneDigits(digits)) return "";

	if (digits.startsWith("55")) {
		const national = digits.slice(2);
		if (national.length === 10) {
			return `+55 (${national.slice(0, 2)}) ${national.slice(2, 6)}-${national.slice(6)}`;
		}
		if (national.length === 11) {
			return `+55 (${national.slice(0, 2)}) ${national.slice(2, 7)}-${national.slice(7)}`;
		}
	}

	if (digits.length === 12) {
		return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
	}
	if (digits.length === 13) {
		return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
	}

	if (digits.length > 2) {
		return `+${digits}`;
	}

	return digits;
}

export function FormField({ id, label, hint, children, className = "mb-4" }) {
	return (
		<div className={className}>
			{label && (
				<label
					htmlFor={id}
					className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
				>
					{label}
				</label>
			)}
			{children}
			{hint && <p className="mt-1 text-sm text-default-500 dark:text-default-400">{hint}</p>}
		</div>
	);
}

export function SegmentControl({ options, value, onChange }) {
	return (
		<div className="inline-flex flex-wrap gap-1 rounded-lg border border-default-200 bg-default-100 p-1 dark:border-default-600 dark:bg-default-800/60">
			{options.map((option) => (
				<button
					key={option.value}
					type="button"
					className={[
						"rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
						value === option.value
							? "bg-primary text-white shadow-sm"
							: "text-default-600 hover:bg-default-200/80 dark:text-default-300 dark:hover:bg-default-700",
					].join(" ")}
					onClick={() => onChange(option.value)}
				>
					{option.label}
				</button>
			))}
		</div>
	);
}

export function AppCheckbox({ checked, onChange, ariaLabel, disabled = false }) {
	return (
		<input
			type="checkbox"
			className="size-4 shrink-0 rounded border-default-300 text-primary focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50 dark:border-default-600"
			checked={checked}
			disabled={disabled}
			onChange={(e) => onChange(e.target.checked)}
			aria-label={ariaLabel}
		/>
	);
}

export function IconSpinner({ className = "app-modal-spinner !size-5" }) {
	return <span className={className} aria-hidden />;
}

export function IconTrash({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

export function IconSearch({ className = "size-3.5" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

export function IconClear({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="18" y1="6" x2="6" y2="18" />
			<line x1="6" y1="6" x2="18" y2="18" />
		</svg>
	);
}

export function IconAttach({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
		</svg>
	);
}

export function IconSmile({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="10" />
			<path d="M8 14s1.5 2 4 2 4-2 4-2" />
			<line x1="9" y1="9" x2="9.01" y2="9" />
			<line x1="15" y1="9" x2="15.01" y2="9" />
		</svg>
	);
}

export function MessageVariableChips({ message }) {
	if (!message) return null;
	return (
		<span className="whitespace-pre-wrap break-words text-sm text-default-800 dark:text-default-200">
			{message.split(" ").map((word, key) =>
				word.startsWith("$") ? (
					<span
						key={key}
						className="mx-0.5 inline-flex rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary"
					>
						{word}
					</span>
				) : (
					<span key={key}>{`${word} `}</span>
				)
			)}
		</span>
	);
}

export function BulkContactsTable({
	columns,
	rows,
	selected,
	isRowSelected,
	onToggle,
	onToggleAll,
	onRemove,
	scrollRef,
	onScroll,
	emptyMessage,
	isRowDisabled,
}) {
	const checkSelected =
		isRowSelected ?? ((row) => selected.includes(row));
	const selectableRows = isRowDisabled
		? rows.filter((row) => !isRowDisabled(row))
		: rows;
	const allSelected =
		selectableRows.length > 0 && selectableRows.every((row) => checkSelected(row));

	return (
		<div
			ref={scrollRef}
			className="app-table-scroll bulk-messaging-contacts-step__table min-h-0 flex-1 overflow-auto rounded-md border border-default-200 dark:border-default-700"
			onScroll={onScroll}
		>
			<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
				<thead className="sticky top-0 z-[1] bg-default-150 dark:bg-default-800/90">
					<tr className="text-xs font-medium uppercase tracking-wide text-default-600 dark:text-default-400">
						<th className="w-10 px-3 py-2.5">
							<AppCheckbox
								checked={allSelected}
								onChange={(checked) => onToggleAll(checked)}
								ariaLabel="Selecionar todos"
							/>
						</th>
						{columns.map((col) => (
							<th key={col.key} className="px-3 py-2.5 text-start">
								{col.label}
							</th>
						))}
						{onRemove && <th className="w-12 px-3 py-2.5" />}
					</tr>
				</thead>
				<tbody className="divide-y divide-default-200 bg-card dark:divide-default-700">
					{rows.length === 0 ? (
						<tr>
							<td
								colSpan={columns.length + (onRemove ? 2 : 1)}
								className="px-3 py-8 text-center text-sm text-default-500"
							>
								{emptyMessage}
							</td>
						</tr>
					) : (
						rows.map((row, index) => {
							const isSelected = checkSelected(row);
							const disabled = isRowDisabled?.(row);
							return (
								<tr
									key={row.id ?? `${row.number}-${index}`}
									className={[
										"text-sm text-default-800 dark:text-default-200",
										disabled && "opacity-50",
									]
										.filter(Boolean)
										.join(" ")}
								>
									<td className="px-3 py-2.5">
										<AppCheckbox
											checked={isSelected}
											onChange={() => !disabled && onToggle(row)}
											ariaLabel="Selecionar contato"
											disabled={disabled}
										/>
									</td>
									{columns.map((col) => (
										<td key={col.key} className="px-3 py-2.5">
											{col.render ? col.render(row) : row[col.key]}
										</td>
									))}
									{onRemove && (
										<td className="px-3 py-2.5 text-end">
											<button
												type="button"
												className="inline-flex size-8 items-center justify-center rounded-md text-danger hover:bg-danger/10"
												onClick={() => onRemove(row)}
												aria-label="Remover"
											>
												<IconTrash />
											</button>
										</td>
									)}
								</tr>
							);
						})
					)}
				</tbody>
			</table>
		</div>
	);
}
